import React, { useEffect, useState } from 'react';
import { dataService } from '../../services/dataService';
import { ControlPlace } from '../../types/domain';

/**
 * What the user has chosen in the hospital picker.
 *
 * 'other' is the escape hatch: the patient's hospital is not on the list yet,
 * so they type it. It is not saved as free text — resolveControlPlace() turns
 * it into a control_places row, either an existing one it matches or a new
 * 'pending' row for an admin to approve or merge (DF-09).
 */
export type ControlPlaceSelection =
  | { kind: 'none' }
  | { kind: 'existing'; id: string; name: string }
  | { kind: 'other'; name: string };

const OTHER_VALUE = '__other__';

export const emptyControlPlaceSelection: ControlPlaceSelection = { kind: 'none' };

/** Resolves a selection to the control_places row to store on the profile. */
export async function resolveControlPlace(
  selection: ControlPlaceSelection
): Promise<{ id: string; name: string }> {
  if (selection.kind === 'existing') {
    return { id: selection.id, name: selection.name };
  }
  if (selection.kind === 'other') {
    const place = await dataService.findOrCreateControlPlace(selection.name);
    return { id: place.id, name: place.name };
  }
  throw new Error('Tempat kontrol belum dipilih.');
}

interface ControlPlaceSelectProps {
  value: ControlPlaceSelection;
  onChange: (value: ControlPlaceSelection) => void;
  id?: string;
}

export const ControlPlaceSelect: React.FC<ControlPlaceSelectProps> = ({
  value,
  onChange,
  id = 'controlPlace'
}) => {
  const [places, setPlaces] = useState<ControlPlace[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let mounted = true;
    dataService
      .getControlPlaces()
      .then(data => {
        if (mounted) {
          setPlaces(data);
        }
      })
      .catch(() => {
        if (mounted) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value;
    if (!next) {
      onChange({ kind: 'none' });
      return;
    }
    if (next === OTHER_VALUE) {
      onChange({ kind: 'other', name: '' });
      return;
    }
    const match = places.find(place => place.id === next);
    if (match) {
      onChange({ kind: 'existing', id: match.id, name: match.name });
    }
  };

  const selectValue =
    value.kind === 'existing' ? value.id : value.kind === 'other' ? OTHER_VALUE : '';

  // A pending place is someone's unreviewed free text, so it is not offered to
  // anyone else. The one exception is the place already chosen on this
  // profile: hiding it would blank the select and lose the patient's answer.
  const visiblePlaces = places.filter(
    place => place.status !== 'pending' || (value.kind === 'existing' && value.id === place.id)
  );

  // If the list cannot be loaded the patient must still be able to register,
  // so fall back to the "Lainnya" path rather than blocking on an empty list.
  if (loadError) {
    return (
      <div className="field">
        <label htmlFor={id}>Tempat Kontrol/Periksa</label>
        <input
          id={id}
          type="text"
          value={value.kind === 'other' ? value.name : ''}
          onChange={event => onChange({ kind: 'other', name: event.target.value })}
          placeholder="Rumah Sakit / Klinik"
        />
        <div className="subtitle" style={{ marginTop: 4, fontSize: 12 }}>
          Daftar tempat kontrol gagal dimuat. Tulis nama tempat kontrolmu.
        </div>
      </div>
    );
  }

  return (
    <div className="field">
      <label htmlFor={id}>Tempat Kontrol/Periksa</label>
      <select id={id} value={selectValue} onChange={handleSelect} disabled={loading}>
        <option value="">{loading ? 'Memuat...' : 'Pilih tempat kontrol'}</option>
        {visiblePlaces.map(place => (
          <option key={place.id} value={place.id}>
            {place.name}
            {place.status === 'pending' ? ' (menunggu persetujuan)' : ''}
          </option>
        ))}
        <option value={OTHER_VALUE}>Lainnya (tulis sendiri)</option>
      </select>

      {value.kind === 'other' ? (
        <>
          <input
            type="text"
            style={{ marginTop: 8 }}
            value={value.name}
            onChange={event => onChange({ kind: 'other', name: event.target.value })}
            placeholder="Tulis nama Rumah Sakit / Klinik"
            aria-label="Nama tempat kontrol lainnya"
          />
          <div className="subtitle" style={{ marginTop: 4, fontSize: 12 }}>
            Tempat baru akan dicek dulu oleh admin sebelum muncul di daftar.
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ControlPlaceSelect;
